import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/utils/api';

export interface Notification {
  id: string;
  type: 'like' | 'dislike' | 'comment' | 'follow' | 'mention' | 'system' | 'profile_purchase' | 'profile_sale' | 'message' | 'spotlight_received' | 'spotlight_sent' | 'royalty_earned' | 'achievement_earned';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  avatar?: string;
  link?: string;
}

export interface NotificationsResponse {
  notifications: Notification[];
  unreadCount: number;
  total: number;
}

// Fetch notifications
export const fetchNotifications = createAsyncThunk(
  'notifications/fetch',
  async ({ type, limit = 50 }: { type?: string; limit?: number } = {}, { rejectWithValue }) => {
    try {
      const params: any = { limit };
      if (type) params.type = type;

      const response = await api.get<NotificationsResponse>('/notifications', { params });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to fetch notifications';
      return rejectWithValue(message);
    }
  }
);

// Mark notification as read
export const markNotificationAsRead = createAsyncThunk(
  'notifications/markRead',
  async (notificationId: string, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/notifications/${notificationId}/read`);
      return { notificationId, ...response.data };
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to mark notification as read';
      return rejectWithValue(message);
    }
  }
);

// Mark all notifications as read
export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllRead',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.patch('/notifications/read-all');
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to mark all as read';
      return rejectWithValue(message);
    }
  }
);

// Delete/dismiss notification
export const dismissNotification = createAsyncThunk(
  'notifications/dismiss',
  async (notificationId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      return notificationId;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to dismiss notification';
      return rejectWithValue(message);
    }
  }
);

// Get unread notification count
export const fetchUnreadNotificationCount = createAsyncThunk(
  'notifications/unreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<{ unreadCount: number }>('/notifications/unread-count');
      return response.data.unreadCount;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to fetch unread count';
      return rejectWithValue(message);
    }
  }
);

// Fetch notifications by type (for tabs)
export const fetchNotificationsByType = createAsyncThunk(
  'notifications/fetchByType',
  async (type: 'all' | 'mentions' | 'likes' | 'follows' | 'system', { rejectWithValue }) => {
    try {
      const typeParam = type === 'all' ? undefined : type;
      const response = await api.get<NotificationsResponse>('/notifications', {
        params: { type: typeParam, limit: 50 }
      });
      return { type, ...response.data };
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to fetch notifications';
      return rejectWithValue(message);
    }
  }
);
